import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { api } from '../../shared/api';

const MAX_FILE_SIZE  = 10 * 1024 * 1024; // 10 MB
const MAX_FILES      = 5;
const ALLOWED_EXTS   = ['.pdf','.png','.jpg','.jpeg','.gif','.webp','.zip','.csv','.xlsx','.docx'];

function validateFiles(files) {
  if (files.length > MAX_FILES) return `Maximum ${MAX_FILES} files allowed.`;
  for (const f of files) {
    if (f.size > MAX_FILE_SIZE) return `"${f.name}" exceeds the 10 MB limit.`;
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'));
    if (!ALLOWED_EXTS.includes(ext)) return `File type "${ext}" is not allowed.`;
  }
  return null;
}

export default function SupportNewTicket() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [title, setTitle]       = useState('');
  const [body, setBody]         = useState('');
  const [priority, setPriority] = useState('medium');
  const [categoryId, setCategoryId] = useState('');
  const [files, setFiles]       = useState([]);
  const [fileError, setFileError] = useState('');

  const { data: categories = [] } = useQuery({
    queryKey: ['support', 'categories'],
    queryFn: () => api.get('support/api/categories/'),
  });

  const createMutation = useMutation({
    mutationFn: (payload) => api.post('support/api/tickets/', payload),
    onSuccess: async (ticket) => {
      // Upload attachments if any
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((f) => fd.append('files', f));
        try {
          await api.post(`support/api/tickets/${ticket.id}/upload/`, fd);
        } catch {
          toast.warn(t('support.uploadWarning', 'Ticket created but attachment upload failed.'));
        }
      }
      toast.success(t('support.ticketCreated', 'Ticket submitted successfully.'));
      navigate(`/support/${ticket.reference}`);
    },
    onError: (err) => {
      if (err?.status === 429) {
        toast.error(t('support.rateLimitError', 'Daily ticket limit reached. Try again tomorrow.'));
      } else {
        toast.error(t('support.createError', 'Failed to create ticket.'));
      }
    },
  });

  function handleFiles(e) {
    const selected = Array.from(e.target.files);
    const err = validateFiles(selected);
    setFileError(err || '');
    if (!err) setFiles(selected);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (fileError) return;

    const payload = {
      title: title.trim(),
      body_html: body.trim(),
      priority,
      ...(categoryId ? { category_id: Number(categoryId) } : {}),
    };
    createMutation.mutate(payload);
  }

  return (
    <div className="page-body">
      <div className="container-xl" style={{ maxWidth: 720 }}>
        <div className="page-header d-print-none">
          <div className="row">
            <div className="col">
              <h2 className="page-title">{t('support.newTicket', 'New support ticket')}</h2>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit}>

              {/* Title */}
              <div className="mb-3">
                <label className="form-label fw-semibold" htmlFor="ticket-title">
                  {t('support.fields.title', 'Subject')} <span className="text-danger">*</span>
                </label>
                <input
                  id="ticket-title"
                  className="form-control"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  maxLength={255}
                  placeholder={t('support.fields.titlePlaceholder', 'Brief description of your issue')}
                />
              </div>

              {/* Priority + Category */}
              <div className="row mb-3 g-3">
                <div className="col-sm-6">
                  <label className="form-label fw-semibold" htmlFor="ticket-priority">
                    {t('support.fields.priority', 'Priority')}
                  </label>
                  <select
                    id="ticket-priority"
                    className="form-select"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="low">{t('support.priority.low', 'Low')}</option>
                    <option value="medium">{t('support.priority.medium', 'Medium')}</option>
                    <option value="high">{t('support.priority.high', 'High')}</option>
                    <option value="urgent">{t('support.priority.urgent', 'Urgent')}</option>
                  </select>
                </div>
                <div className="col-sm-6">
                  <label className="form-label fw-semibold" htmlFor="ticket-category">
                    {t('support.fields.category', 'Category')}
                  </label>
                  <select
                    id="ticket-category"
                    className="form-select"
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                  >
                    <option value="">{t('support.fields.categoryNone', '— None —')}</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Body */}
              <div className="mb-3">
                <label className="form-label fw-semibold" htmlFor="ticket-body">
                  {t('support.fields.body', 'Description')} <span className="text-danger">*</span>
                </label>
                <textarea
                  id="ticket-body"
                  className="form-control"
                  rows={6}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  required
                  placeholder={t('support.fields.bodyPlaceholder', 'Describe your issue in detail…')}
                />
              </div>

              {/* Attachments */}
              <div className="mb-4">
                <label className="form-label fw-semibold" htmlFor="ticket-files">
                  {t('support.fields.attachments', 'Attachments')}
                  <span className="text-muted fw-normal ms-2 small">
                    {t('support.fields.attachmentsHint', '(max 5 files, 10 MB each)')}
                  </span>
                </label>
                <input
                  id="ticket-files"
                  type="file"
                  className={`form-control ${fileError ? 'is-invalid' : ''}`}
                  multiple
                  accept={ALLOWED_EXTS.join(',')}
                  onChange={handleFiles}
                />
                {fileError && <div className="invalid-feedback">{fileError}</div>}
                {files.length > 0 && !fileError && (
                  <ul className="mt-2 mb-0 small text-muted">
                    {files.map((f) => (
                      <li key={f.name}>{f.name} ({(f.size / 1024).toFixed(0)} KB)</li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Submit */}
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/support')}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending || !!fileError}
                >
                  {createMutation.isPending
                    ? t('common.sending', 'Sending…')
                    : t('support.submitTicket', 'Submit ticket')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
